/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import PendingModifications from './PlacementModsGrid/PendingModifications';
import PlacementHistory from './PlacementModsGrid/PlacementHistory';
import { PlacementModsDialog } from './CommonComponents/PlacementModsDialog';
import { useDispatch, useSelector } from 'react-redux';
import { getModName, getSelectedModDetails } from './ModificationsUtils';
import { useParams } from 'react-router-dom';
import {
  selectCurrentModDetails,
  selectDefaultModDetails,
  selectIsOpenModForAdding,
  selectModSavedStatus,
} from 'store/redux-store/placement-mods/selectors';
import { selectPlacementStatus } from 'store/redux-store/placement-status/selectors';
import { selectPlacementModificationTabRendered, selectPlacementHeaderData } from 'store/redux-store/placement-details/selectors';
import { placementDetailsAction } from 'store/redux-store/placement-details/slice';
import { isClientContractAdminUser, isPayrollSpecialist, isReadOnly, isRecruitment } from 'oidc/userRoles';
import { selectUser } from 'oidc/user.selectors';
import { placementModsAction } from 'store/redux-store/placement-mods/slice';
import { AcceptenceStatus, ApprovalStatus, IPlacementModification, MODIFICATIONS_GRID_PAGE_SIZE } from 'app/models/Placement/PlacementMods';
import { useTranslation } from 'react-i18next';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { gridSelectionActions } from '@AMIEWEB/Common/Grid/GridSelection/GridSelection.redux';
import { ff_placement_ui_mod_edit_all_roles } from 'app/constants/FeatureFlags/Placement/Keys';
import { useDecision } from '@optimizely/react-sdk';
import { GridType } from 'app/components/Common/types';

const ModificationsTab = () => {
  const defaultModDetails = useSelector(selectDefaultModDetails);
  const selectedModsDetails = useSelector(selectCurrentModDetails);
  const modSavedResponse = useSelector(selectModSavedStatus);
  const { currentPlacementStatus } = useSelector(selectPlacementStatus);
  const placementHeaderData = useSelector(selectPlacementHeaderData);
  const tabRendered = useSelector(selectPlacementModificationTabRendered);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [modsDialogDetails, setModsDialogDetails] = useState({
    isOpen: false,
    selectedMod: null,
  });
  const [selectedRowDetails, setSelectedRowDetails] = useState<any>({});
  const isModelOpenForAdding = useSelector(selectIsOpenModForAdding);
  const appUser = useSelector(selectUser);
  const param = useParams<{ placementId: string }>();

  const isDateOrShiftMod = React.useCallback(selectedRow => {
    const currentMod = selectedRow?.type?.replaceAll(/['.\s]/g, '');
    return ['StartDate', 'EndDate', 'Shift', 'Timeoff'].includes(currentMod);
  }, []);
  const [modEditFeatureFlag] = useDecision(ff_placement_ui_mod_edit_all_roles);

  // Track previous placement status to detect changes
  const prevPlacementStatusRef = React.useRef(currentPlacementStatus);
  const prevHeaderStatusRef = React.useRef(placementHeaderData?.activityStatusId);

  const isApprovedOrDenied = React.useCallback((statusId: ApprovalStatus) => {
    return statusId === ApprovalStatus.Approved || statusId === ApprovalStatus.Denied;
  }, []);

  const isApprovedAndAccepted = React.useCallback((modDetails: IPlacementModification) => {
    return (
      modDetails?.approvalStatusId === ApprovalStatus.Approved &&
      modDetails?.acceptanceStatusId === AcceptenceStatus.Accepted &&
      modDetails?.voidStatus === 'false'
    );
  }, []);

  const setModificationState = React.useCallback(
    (isEdit: boolean, isExisting: boolean, changeToPending: boolean = false) => {
      dispatch(placementModsAction.setIsModEdit(isEdit));
      dispatch(placementModsAction.setIsEditExistingMod(isExisting));
      if (changeToPending) {
        dispatch(placementModsAction.setChangeToPending(true));
      }
    },
    [dispatch],
  );

  const onModsDetailsClick = (params, click) => {
    if (click === 'single') {
      if (params.field === 'type') {
        setModsDialogDetails({
          isOpen: true,
          selectedMod: params.row.type,
        });
      }
    } else {
      setModsDialogDetails({
        isOpen: true,
        selectedMod: params.row.type,
      });
    }
    setSelectedRowDetails(params.row);
    getSelectedModDetails(params.row, dispatch, param.placementId);
  };

  const getFieldAndStatusEnableLogic = () => {
    const currentMod = selectedRowDetails?.type?.replaceAll(/['.\s]/g, '');
    const userRoles = appUser?.userInfo?.roles || [];

    // Rule: New Modification
    if (isModelOpenForAdding) {
      setModificationState(true, false);
      return;
    }

    // Rule: Placement Negative Status(e.g Declined by Facility) Or Read Only User
    if (selectedModsDetails?.isNegativeStatus || isReadOnly(userRoles)) {
      setModificationState(false, false);
      return;
    }

    // Rule: Placement Pre & Post Approval Booking Logic
    if (selectedModsDetails.isBookingApprovedOrGreater === true) {
      if (
        isApprovedOrDenied(selectedModsDetails?.approvalStatusId) ||
        (currentMod === 'EndDate' && selectedModsDetails?.approvalStatusId === ApprovalStatus.RecruiterApproved)
      ) {
        setModificationState(false, true, true);
      } else if (
        isApprovedAndAccepted(selectedModsDetails) &&
        isPayrollSpecialist(userRoles) &&
        currentMod === 'Bonus'
      ) {
        setModificationState(false, true);
      }
    } else {
      if (isDateOrShiftMod(selectedRowDetails) && isApprovedOrDenied(selectedModsDetails?.approvalStatusId)) {
        setModificationState(false, false);
      } else {
        dispatch(placementModsAction.setIsModEdit(true));
        dispatch(placementModsAction.setIsEditExistingMod(true));
        if (!isApprovedOrDenied(selectedModsDetails?.approvalStatusId)) {
          setModificationState(true, true, getModName(selectedRowDetails) !== 'Preferences');
        }
      }
    }

    // Rule: Pending Status Override.
    if (selectedModsDetails?.approvalStatusId === ApprovalStatus.Pending && !isReadOnly(userRoles)) {
      setModificationState(true, true);
    }
  };
  const getFieldAndStatusEnableLogicOld = () => {
    const currentMod = selectedRowDetails?.type?.replaceAll(/['.\s]/g, '');
    if (isModelOpenForAdding) {
      dispatch(placementModsAction.setIsModEdit(true));
      dispatch(placementModsAction.setIsEditExistingMod(false));
    } else if (
      selectedModsDetails?.isNegativeStatus ||
      isReadOnly(appUser?.userInfo?.roles || []) ||
      !isRecruitment(appUser?.userInfo?.roles || [])
    ) {
      dispatch(placementModsAction.setIsModEdit(false));
      dispatch(placementModsAction.setIsEditExistingMod(false));
    } else if (selectedModsDetails.isBookingApprovedOrGreater === false) {
      if (
        isDateOrShiftMod(selectedRowDetails) &&
        (selectedModsDetails?.approvalStatusId === ApprovalStatus.Approved ||
          selectedModsDetails?.approvalStatusId === ApprovalStatus.Denied)
      ) {
        dispatch(placementModsAction.setIsModEdit(false));
        dispatch(placementModsAction.setIsEditExistingMod(true));
      } else {
        dispatch(placementModsAction.setIsModEdit(true));
        dispatch(placementModsAction.setIsEditExistingMod(true));

        if (getModName(selectedRowDetails) !== 'Preferences') {
          dispatch(placementModsAction.setChangeToPending(true));
        }
      }
    } else if (selectedModsDetails.isBookingApprovedOrGreater === true && !isModelOpenForAdding) {
      if (
        selectedModsDetails?.approvalStatusId === ApprovalStatus.Approved ||
        selectedModsDetails?.approvalStatusId === ApprovalStatus.Denied ||
        (currentMod === 'EndDate' && selectedModsDetails?.approvalStatusId === ApprovalStatus.RecruiterApproved)
      ) {
        dispatch(placementModsAction.setIsModEdit(false));
        dispatch(placementModsAction.setChangeToPending(true));
        dispatch(placementModsAction.setIsEditExistingMod(true));
      } else {
        dispatch(placementModsAction.setIsModEdit(true));
        dispatch(placementModsAction.setIsEditExistingMod(true));
      }
    }
    if (
      currentMod === 'BillRate' &&
      !isClientContractAdminUser(appUser?.userInfo?.roles || []) &&
      !isModelOpenForAdding
    ) {
      dispatch(placementModsAction.setIsModEdit(false));
      dispatch(placementModsAction.setIsEditExistingMod(false));
    }
    if (
      currentMod === 'PayrollShiftDifferential' &&
      !isModelOpenForAdding &&
      isClientContractAdminUser(appUser?.userInfo?.roles || [])
    ) {
      dispatch(placementModsAction.setIsModEdit(false));
      dispatch(placementModsAction.setIsEditExistingMod(false));
    }

    if (
      currentMod === 'BillingShiftDifferential' &&
      (isReadOnly(appUser?.userInfo?.roles || []) ||
        (!isClientContractAdminUser(appUser?.userInfo?.roles || []) && !isModelOpenForAdding))
    ) {
      dispatch(placementModsAction.setIsModEdit(false));
      dispatch(placementModsAction.setIsEditExistingMod(false));
    }
    if (
      selectedModsDetails.isBookingApprovedOrGreater === true &&
      selectedModsDetails?.approvalStatusId === ApprovalStatus.Approved &&
      selectedModsDetails?.acceptanceStatusId === AcceptenceStatus.Accepted &&
      selectedModsDetails?.voidStatus === false &&
      isPayrollSpecialist(appUser?.userInfo?.roles || []) &&
      currentMod === 'Bonus'
    ) {
      dispatch(placementModsAction.setIsModEdit(false));
      dispatch(placementModsAction.setIsEditExistingMod(true));
    }
    if (
      (selectedModsDetails.isBookingApprovedOrGreater === false &&
        getModName(selectedRowDetails) === 'Bonus' &&
        selectedModsDetails.standard) ||
      selectedModsDetails?.isNegativeStatus
    ) {
      dispatch(placementModsAction.setStandard(true));
    } else {
      dispatch(placementModsAction.setStandard(false));
    }
  };
  useEffect(() => {
    if (isModelOpenForAdding) setSelectedRowDetails({ ...selectedRowDetails, type: isModelOpenForAdding?.selectedMod });
    return () => {
      dispatch(
        gridSelectionActions.setGridSelections({
          selectedData: [],
        }),
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModelOpenForAdding]);

  useEffect(() => {
    if (selectedModsDetails?.approvalStatusId) {
      if (modEditFeatureFlag.enabled) {
        getFieldAndStatusEnableLogic();
      } else {
        getFieldAndStatusEnableLogicOld();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModsDetails?.approvalStatusId]);

  useEffect(() => {
    if (modSavedResponse?.isModDeleted) {
      dispatch(
        globalActions.setStackSnackBar({
          message: modSavedResponse.updateMessage,
          position: 'top',
          severity: 'success',
        }),
      );
      dispatch(
        globalActions.setStackSnackBar({
          message: t('placement.placementMods.placementModsAPIMessages.deleteModLinkUnlink'),
          severity: 'success',
        }),
      );
    }
  }, [modSavedResponse?.isModDeleted]);

  useEffect(() => {
    const placementId = parseInt(param.placementId);
    
    dispatch(placementModsAction.getPlacementModsList({ placementId }));
    dispatch(placementModsAction.getRefreshPlacementModsList({ placementId }));
    dispatch(placementModsAction.getStartDateReasonCodes());
    dispatch(placementModsAction.getInitiators({ placementId: param.placementId }));
    
    dispatch(placementModsAction.setPendingMods([]));
    dispatch(placementModsAction.setModificationHistory([]));
    
    const pendingPayload = {
      pageNumber: 1,
      pageSize: MODIFICATIONS_GRID_PAGE_SIZE,
      placementId: placementId,
      offSet: 0,
      gridType: GridType.modification,
      sortColumn: null,
      sortOrder: null,
    };
    dispatch(placementModsAction.getModificationAction(pendingPayload));
    
    const loadHistoryData = () => {
      const historyPayload = {
        pageNumber: 1,
        pageSize: MODIFICATIONS_GRID_PAGE_SIZE,
        placementId: placementId,
        offSet: 0,
        gridType: null, 
        sortColumn: null,
        sortOrder: null,
      };
      dispatch(placementModsAction.getModificationAction(historyPayload));
    };
    
    setTimeout(loadHistoryData, 600); // Increased delay to 600ms
  }, []);

  // Listen for placement status changes and refresh both modifications grids
  useEffect(() => {
    // Check if placement status has actually changed (not just initial load)
    const currentStatusId = currentPlacementStatus?.activityStatusId;
    const prevStatusId = prevPlacementStatusRef.current?.activityStatusId;
    const headerStatusId = placementHeaderData?.activityStatusId;
    const prevHeaderStatusId = prevHeaderStatusRef.current;

    // only trigger if we have both current and previous values (avoiding initial load)
    // skip if this is the first time we're setting the refs (initial load)
    if (
      (currentStatusId && prevStatusId && currentStatusId !== prevStatusId) ||
      (headerStatusId && prevHeaderStatusId && headerStatusId !== prevHeaderStatusId)
    ) {
      
      dispatch(placementModsAction.setPendingMods([]));
      dispatch(placementModsAction.setModificationHistory([]));
      
      const placementId = parseInt(param.placementId);
      
      // Refresh the list of available modifications when status changes
      dispatch(placementModsAction.getPlacementModsList({ placementId }));
        
      // Also refresh the checkbox specs to ensure proper state
      dispatch(placementModsAction.getBookingApprovedCheckboxSpecs({ placementId }));
      
      const pendingPayload = {
        pageNumber: 1,
        pageSize: MODIFICATIONS_GRID_PAGE_SIZE,
        placementId: placementId,
        offSet: 0,
        gridType: GridType.modification,
        sortColumn: null,
        sortOrder: null,
      };
      dispatch(placementModsAction.getModificationAction(pendingPayload));
      
      const loadHistoryData = () => {
        const historyPayload = {
          pageNumber: 1,
          pageSize: MODIFICATIONS_GRID_PAGE_SIZE,
          placementId: placementId,
          offSet: 0,
          gridType: null, 
          sortColumn: null,
          sortOrder: null,
        };
        dispatch(placementModsAction.getModificationAction(historyPayload));
      };
      
      setTimeout(loadHistoryData, 600); // Increased delay to 600ms
    }
    
    // Update the refs with current status
    prevPlacementStatusRef.current = currentPlacementStatus;
    prevHeaderStatusRef.current = placementHeaderData?.activityStatusId;
  }, [currentPlacementStatus, placementHeaderData?.activityStatusId, dispatch, param.placementId]);

  // Handle tab re-render to reload grid data
  useEffect(() => {
    if (!tabRendered) {
      // This will trigger both grids to reload their data
      dispatch(placementDetailsAction.setModificationTabRendered(true));
    }
  }, [tabRendered, dispatch]);

  return (
    <React.Fragment>
      <Grid container direction="column" spacing={7}>
        <Grid item xs={12} md={12}>
          <PendingModifications 
            onModsDetailsClick={onModsDetailsClick} 
            setModsDialogDetails={setModsDialogDetails}
          />
        </Grid>
        <Grid item xs={12} md={12}>
          <PlacementHistory 
            onModsDetailsClick={onModsDetailsClick}
          />
        </Grid>
      </Grid>
      {modsDialogDetails?.isOpen && (
        <>
          <PlacementModsDialog
            modsDialogDetails={modsDialogDetails}
            setModsDialogDetails={setModsDialogDetails}
            selectedRowDetails={selectedRowDetails}
            defaultModDetails={defaultModDetails}
            setSelectedRowDetails={setSelectedRowDetails}
          />
        </>
      )}
    </React.Fragment>
  );
};

export default ModificationsTab;
